import { matchPath } from "react-router-dom";
import type { Params } from "react-router-dom";
import { urls } from "../../urls";
import type { WireMessage } from "./agentMemory";

export type PageEntityKind = "contract" | "v2Event" | "project" | "need" | "organization";

export type PageEntity = { kind: PageEntityKind; id: string };

export type PageContext = { description: string; entity?: PageEntity };

export const PAGE_CONTEXT_PREFIX = "Brukeren ser nå på:";

type Lookups = { entityLabel: (id: string) => string | undefined };

type EntityKind = PageEntityKind;

const ENTITY_PHRASES: Record<EntityKind, { noun: string; referenceClause: string; pronoun: string }> = {
  contract: { noun: "Kontrakt", referenceClause: "«denne kontrakten»", pronoun: "denne" },
  v2Event: { noun: "V2-anskaffelse", referenceClause: "«denne anskaffelsen»", pronoun: "denne" },
  project: { noun: "Prosjekt", referenceClause: "«dette prosjektet»", pronoun: "dette" },
  need: { noun: "Behov", referenceClause: "«dette behovet»", pronoun: "dette" },
  organization: { noun: "Bedrift", referenceClause: "«denne bedriften» eller «dette selskapet»", pronoun: "denne" },
};

const entityContext = (kind: EntityKind, id: string, lookups: Lookups, idLabel = "id"): PageContext => {
  const { noun, referenceClause, pronoun } = ENTITY_PHRASES[kind];
  const label = lookups.entityLabel(id);
  const subject = label ? `${noun} «${label}» (${idLabel} ${id})` : `${noun} med ${idLabel} ${id}`;
  return {
    description: `${PAGE_CONTEXT_PREFIX} ${subject}. Når brukeren sier ${referenceClause}, er det ${pronoun}.`,
    entity: { kind, id },
  };
};

const pageOnlyContext = (pageLabel: string): PageContext => ({
  description: `${PAGE_CONTEXT_PREFIX} ${pageLabel}.`,
});

type Matcher = { pattern: string; resolve: (params: Params, search: string, lookups: Lookups) => PageContext | null };

const entity = (pattern: string, kind: EntityKind, idParam: string): Matcher => ({
  pattern,
  resolve: (params, _search, lookups) => {
    const id = params[idParam];
    return id ? entityContext(kind, id, lookups) : null;
  },
});

const entityFromQuery = (pattern: string, kind: EntityKind, idParams: string[]): Matcher => ({
  pattern,
  resolve: (_params, search, lookups) => {
    const query = new URLSearchParams(search);
    const idParam = idParams.find((param) => query.get(param));
    const id = idParam ? query.get(idParam) : null;
    return id && idParam ? entityContext(kind, id, lookups, idParam) : null;
  },
});

const page = (pattern: string, pageLabel: string): Matcher => ({
  pattern,
  resolve: () => pageOnlyContext(pageLabel),
});

const none = (pattern: string): Matcher => ({ pattern, resolve: () => null });

const MATCHERS: Matcher[] = [
  none(urls.app.assistant),
  page(urls.home, "forsiden"),
  page(urls.app.home, "forsiden"),
  page(urls.contracts.viewAll, "kontraktsoversikten"),
  page(urls.contracts.viewDrafts, "kontraktsoversikten (utkast)"),
  page(urls.contracts.viewPublished, "kontraktsoversikten (publiserte)"),
  page(urls.contracts.search, "kontraktsøket"),
  page(urls.contracts.viewTasks, "oppgaveoversikten for kontrakter"),
  page(urls.contracts.settings, "kontraktsinnstillingene"),
  page(urls.myProfile.organization.settings, "innstillingene"),
  page(urls.app.settings, "innstillingene"),
  page(urls.app.contracts.viewAll, "kontraktsoversikten"),
  page(urls.app.contracts.active, "kontraktsoversikten (aktive)"),
  page(urls.app.contracts.tasks, "oppgaveoversikten for kontrakter"),
  page(urls.app.sourcingEvents.viewAll, "oversikten over anskaffelser"),
  page(urls.projects.viewAll, "prosjektoversikten"),
  page(urls.app.projects.viewAll, "prosjektoversikten"),
  page(urls.app.needs.list, "behovslista"),
  page(urls.app.organizations, "bedriftsoversikten"),
  entityFromQuery(`${urls.app.organizationProfile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entityFromQuery(`${urls.organizations.profile.pathname}/:section`, "organization", ["entryId", "orgId"]),
  entity(urls.contracts.viewSingle.pathname, "contract", "contractId"),
  entity(urls.contracts.edit.paths.details.pathname, "contract", "contractId"),
  entity(urls.app.contracts.view.pathname, "contract", "contractId"),
  entity(urls.app.contracts.edit.pathname, "contract", "contractId"),
  entity(urls.app.sourcingEvents.view.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.protocol.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.bidDetail.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.participationRequestDetail.pathname, "v2Event", "eventId"),
  entity(urls.app.sourcingEvents.edit.paths.info.pathname, "v2Event", "eventId"),
  entity(urls.projects.view.pathname, "project", "projectId"),
  entity(urls.app.projects.view.pathname, "project", "projectId"),
  entity(urls.app.needs.view.pathname, "need", "needId"),
  entity(urls.app.needs.edit.pathname, "need", "needId"),
];

export const resolvePageContext = (pathname: string, search: string, lookups: Lookups): PageContext | null => {
  for (const { pattern, resolve } of MATCHERS) {
    const match = matchPath(pattern, pathname);
    if (match) {
      return resolve(match.params, search, lookups);
    }
  }
  return null;
};

export const withPageContext = (messages: WireMessage[], context: PageContext | null): WireMessage[] =>
  context ? [{ role: "system", content: context.description }, ...messages] : messages;
